import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { CreateTeamModal } from './create-team-modal/create-team-modal';
import { TournamentService } from '../../core/services/tournament.service';
import { BranchService } from '../../core/services/branch.service';
import {
  TournamentDetail as TournamentDetailModel,
  UpdateTournamentRequest,
  TournamentBasicResponse,
  TeamRegistrationsResponse,
  IndividualRegistrationsResponse,
  TeamRegistration
} from '../../core/models/tournament-detail.model';
import { UpdateTeamRegistrationRequest, AddPlayerToTeamRequest } from '../../core/models/create-team-from-individuals.model';
import { TeamRegistrationRequest, IndividualRegistrationRequest, PlayerDto } from '../../core/models/registration.model';
import { Branch } from '../../core/models/branch.model';
import { PaymentStatus, RecordPaymentRequest, PAYMENT_METHODS, PaymentSummaryResponse } from '../../core/models/payment.model';
import { Match, CreateMatchRequest, UpdateMatchRequest, GenerateGroupMatchesRequest, MATCH_STAGES, MATCH_STATUSES } from '../../core/models/match.model';
import { TournamentGroup } from '../../core/models/group.model';
import { BracketComponent } from './bracket/bracket.component';

@Component({
  selector: 'app-tournament-detail',
  imports: [CommonModule, FormsModule, MatTabsModule, MatAutocompleteModule, MatFormFieldModule, MatInputModule, MatSelectModule, Sidebar, CreateTeamModal, BracketComponent],
  templateUrl: './tournament-detail.html',
  styleUrl: './tournament-detail.scss',
})
export class TournamentDetailPage implements OnInit {
  tournamentId: string = '';
  tournament: TournamentDetailModel | null = null; // Keep for backward compatibility during migration

  // Separate state for different data pieces
  tournamentBasics: TournamentBasicResponse | null = null;
  teamRegistrationsData: TeamRegistrationsResponse | null = null;
  individualRegistrationsData: IndividualRegistrationsResponse | null = null;

  // Loading states per section
  isLoading: boolean = false; // Keep for overall loading state
  isLoadingBasics: boolean = false;
  isLoadingTeams: boolean = false;
  isLoadingIndividuals: boolean = false;

  // Cache flags to avoid reloading
  teamsLoaded: boolean = false;
  individualsLoaded: boolean = false;

  isEditing: boolean = false;
  isSaving: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  activeTab: 'details' | 'teams' | 'individuals' | 'create-team' | 'matches' = 'details';
  tabIndex = 0;
  private readonly tabMap: ('details' | 'teams' | 'individuals' | 'create-team' | 'matches')[] =
    ['details', 'teams', 'individuals', 'create-team', 'matches'];
  expandedTeams: Set<string> = new Set();

  // Payment filtering
  selectedPaymentStatus: PaymentStatus | null = null;
  PaymentStatus = PaymentStatus;

  // Team pagination
  teamCurrentPage: number = 1;
  teamPageSize: number = 10;
  teamTotalPages: number = 0;

  // Individual pagination
  individualCurrentPage: number = 1;
  individualPageSize: number = 10;
  individualTotalPages: number = 0;

  // Branch filter
  selectedBranch: string | null = null;
  availableBranches: string[] = [];

  // Payment modal
  showPaymentModal: boolean = false;
  selectedRegistrationId: string = '';
  selectedRegistrationType: 'team' | 'individual' = 'team';
  isRecordingPayment: boolean = false;
  paymentErrorMessage: string = '';
  paymentMethods = PAYMENT_METHODS;
  paymentForm: RecordPaymentRequest = {
    amount: 0,
    paymentMethod: 'Cash',
    transactionReference: undefined,
    notes: undefined,
    paymentDate: new Date().toISOString().split('T')[0]
  };

  // Team registration modal
  showTeamModal: boolean = false;

  // Create team from individuals modal
  showCreateTeamModal: boolean = false;
  isSubmittingTeam: boolean = false;
  teamForm: TeamRegistrationRequest = this.getEmptyTeamForm();

  // Individual registration modal
  showIndividualModal: boolean = false;
  isSubmittingIndividual: boolean = false;
  individualForm: IndividualRegistrationRequest = this.getEmptyIndividualForm();

  // Branches list
  branches: Branch[] = [];

  // Edit form
  editForm: UpdateTournamentRequest = {
    name: '',
    sport: '',
    description: null,
    startDate: '',
    endDate: '',
    branch: '',
    maxTeams: 0,
    maxIndividuals: 0,
    teamRegistrationFee: 0,
    teamRegistrationFeeAcademy: 0,
    individualRegistrationFee: 0,
    individualRegistrationFeeAcademy: 0,
    showOnMobileHome: false,
    isIndividualRegistrationEnabled: true,
    isTeamRegistrationEnabled: true
  };

  // File upload
  selectedFile: File | null = null;
  isUploadingBanner: boolean = false;

  // Detail panel state
  showDetailPanel: boolean = false;
  selectedRegistration: any = null;
  selectedRegistrationTypeForDetail: 'team' | 'individual' | null = null;
  isEditingRegistration: boolean = false;

  // Form state for editing registration
  registrationEditForm: any = {
    // Team fields
    teamName: '',
    jerseyColor: '',
    nearestBranch: '',
    isAcademyMember: false,
    // Individual fields
    fullName: '',
    gender: '',
    dateOfBirth: null,
    preferredPosition: '',
    skillLevel: '',
    phoneNumber: '',
  };

  // Add player form state
  showAddPlayerForm: boolean = false;
  isAddingPlayer: boolean = false;
  isSavingRegistration: boolean = false;
  addPlayerForm: AddPlayerToTeamRequest = {
    fullName: '',
    gender: null,
    dateOfBirth: null,
    preferredPosition: null,
    skillLevel: null,
    phoneNumber: null,
    isCaptain: false,
  };

  // Payment summary state
  paymentSummary: PaymentSummaryResponse | null = null;
  isLoadingPaymentSummary: boolean = false;

  // Matches state
  matches: Match[] = [];
  isLoadingMatches: boolean = false;
  matchesLoaded: boolean = false;
  matchStages = MATCH_STAGES;
  matchStatuses = MATCH_STATUSES;

  showMatchModal: boolean = false;
  isEditingMatch: boolean = false;
  isSavingMatch: boolean = false;
  matchErrorMessage: string = '';
  selectedMatch: Match | null = null;
  matchForm: CreateMatchRequest = this.getEmptyMatchForm();
  matchEditForm: UpdateMatchRequest & { homeTeamName?: string; awayTeamName?: string; } = {};

  // Registered teams for match team selection dropdowns
  registeredTeams: TeamRegistration[] = [];
  isLoadingRegisteredTeams: boolean = false;
  registeredTeamsLoaded: boolean = false;
  homeTeamFilter: string = '';
  awayTeamFilter: string = '';

  // Generate group state
  showGroupModal: boolean = false;
  isGeneratingGroup: boolean = false;
  groupErrorMessage: string = '';
  groupForm: { groupName: string; stage: string; selectedTeamIds: Set<string> } = { groupName: '', stage: 'Group Stage', selectedTeamIds: new Set() };
  groupPreview: { home: string; away: string }[] = [];
  groups: TournamentGroup[] = [];

  // Match view mode
  matchViewMode: 'list' | 'bracket' = 'list';

  // Match list filters
  matchFilterStage: string = '';
  matchFilterGroup: string = '';
  matchFilterTeam: string = '';
  matchFilterTeamSearch: string = '';
  matchFilterDate: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tournamentService: TournamentService,
    private branchService: BranchService
  ) {}

  ngOnInit() {
    this.tournamentId = this.route.snapshot.params['id'];
    this.loadBranches();
    this.loadTournamentBasics();

    // Check if user is navigating to a specific tab
    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    if (tabParam && this.tabMap.includes(tabParam as any)) {
      this.setTabByName(tabParam as any);
    }

    // Load data for initial tab if not details
    if (this.activeTab === 'teams') {
      this.loadTeamRegistrations();
    } else if (this.activeTab === 'individuals') {
      this.loadIndividualRegistrations();
    }
  }

  loadTournamentBasics() {
    this.isLoadingBasics = true;
    this.errorMessage = '';
    this.tournamentService.getTournamentDetails(this.tournamentId).subscribe({
      next: (response) => {
        this.tournamentBasics = response.data;
        this.isLoadingBasics = false;

        // Load payment summary for Details tab
        this.loadPaymentSummary();

        // Switch to details tab if current tab is disabled
        if ((this.activeTab === 'individuals' || this.activeTab === 'create-team') &&
            !response.data.isIndividualRegistrationEnabled) {
          this.setTabByName('details');
        }

        this.initEditForm();
      },
      error: (error) => {
        console.error('Error loading tournament basics:', error);
        this.errorMessage = error.error?.message || 'Failed to load tournament details';
        this.isLoadingBasics = false;
      }
    });
  }

  loadTeamRegistrations() {
    this.isLoadingTeams = true;
    this.tournamentService.getTeamRegistrations(
      this.tournamentId,
      this.selectedPaymentStatus ?? undefined,
      this.selectedBranch ?? undefined,
      this.teamCurrentPage,
      this.teamPageSize
    ).subscribe({
      next: (response) => {
        this.teamRegistrationsData = response.data;
        this.teamTotalPages = response.data.registrations.totalPages;
        this.teamsLoaded = true;
        this.isLoadingTeams = false;

        // Update available branches from team data
        this.updateAvailableBranches();
      },
      error: (error) => {
        console.error('Error loading team registrations:', error);
        this.isLoadingTeams = false;
      }
    });
  }

  loadIndividualRegistrations() {
    this.isLoadingIndividuals = true;
    this.tournamentService.getIndividualRegistrations(
      this.tournamentId,
      this.selectedPaymentStatus ?? undefined,
      this.selectedBranch ?? undefined,
      this.individualCurrentPage,
      this.individualPageSize
    ).subscribe({
      next: (response) => {
        this.individualRegistrationsData = response.data;
        this.individualTotalPages = response.data.registrations.totalPages;
        this.individualsLoaded = true;
        this.isLoadingIndividuals = false;

        // Update available branches from individual data
        this.updateAvailableBranches();
      },
      error: (error) => {
        console.error('Error loading individual registrations:', error);
        this.isLoadingIndividuals = false;
      }
    });
  }

  updateAvailableBranches() {
    const branches = new Set<string>();

    if (this.teamRegistrationsData) {
      this.teamRegistrationsData.registrations.registrations.forEach(t => {
        if (t.nearestBranch) branches.add(t.nearestBranch);
      });
    }

    if (this.individualRegistrationsData) {
      this.individualRegistrationsData.registrations.registrations.forEach(i => {
        if (i.nearestBranch) branches.add(i.nearestBranch);
      });
    }

    this.availableBranches = Array.from(branches).sort();
  }

  // Keep old method for backward compatibility during migration
  loadTournament() {
    this.loadTournamentBasics();
    if (this.activeTab === 'teams') {
      this.loadTeamRegistrations();
    } else if (this.activeTab === 'individuals') {
      this.loadIndividualRegistrations();
    }
  }

  loadBranches() {
    this.branchService.getBranches().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.branches = response.data.filter(b => b.isActive);
        }
      },
      error: (error) => {
        console.error('Error loading branches:', error);
      }
    });
  }

  initEditForm() {
    if (!this.tournamentBasics) return;

    this.editForm = {
      name: this.tournamentBasics.name,
      sport: this.tournamentBasics.sport,
      description: this.tournamentBasics.description,
      startDate: this.tournamentBasics.startDate.split('T')[0],
      endDate: this.tournamentBasics.endDate.split('T')[0],
      branch: this.tournamentBasics.branch,
      maxTeams: this.tournamentBasics.maxTeams,
      maxIndividuals: this.tournamentBasics.maxIndividuals,
      teamRegistrationFee: this.tournamentBasics.teamRegistrationFee,
      teamRegistrationFeeAcademy: this.tournamentBasics.teamRegistrationFeeAcademy,
      individualRegistrationFee: this.tournamentBasics.individualRegistrationFee,
      individualRegistrationFeeAcademy: this.tournamentBasics.individualRegistrationFeeAcademy,
      showOnMobileHome: this.tournamentBasics.showOnMobileHome,
      isIndividualRegistrationEnabled: this.tournamentBasics.isIndividualRegistrationEnabled,
      isTeamRegistrationEnabled: this.tournamentBasics.isTeamRegistrationEnabled
    };
  }

  toggleEdit() {
    if (this.isEditing) {
      // Cancel editing - reset form
      this.initEditForm();
    }
    this.isEditing = !this.isEditing;
    this.errorMessage = '';
    this.successMessage = '';
  }

  saveTournament() {
    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request: UpdateTournamentRequest = {
      ...this.editForm,
      startDate: new Date(this.editForm.startDate).toISOString(),
      endDate: new Date(this.editForm.endDate).toISOString()
    };

    this.tournamentService.updateTournament(this.tournamentId, request).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.success) {
          this.successMessage = 'Tournament updated successfully';
          this.isEditing = false;
          this.loadTournamentBasics(); // Only reload basics, keep registration tabs cached
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isSaving = false;
        this.errorMessage = error.error?.message || 'Failed to update tournament';
        console.error('Error updating tournament:', error);
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        this.errorMessage = 'Only PNG and JPG files are allowed';
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = 'File size must be less than 10MB';
        return;
      }

      this.selectedFile = file;
      this.uploadBanner();
    }
  }

  uploadBanner() {
    if (!this.selectedFile) return;

    this.isUploadingBanner = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.tournamentService.uploadBanner(this.tournamentId, this.selectedFile).subscribe({
      next: (response) => {
        this.isUploadingBanner = false;
        if (response.success) {
          this.successMessage = 'Banner uploaded successfully';
          this.selectedFile = null;
          this.loadTournamentBasics(); // Only reload basics to update banner URL
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isUploadingBanner = false;
        this.errorMessage = error.error?.message || 'Failed to upload banner';
        console.error('Error uploading banner:', error);
      }
    });
  }

  onTabIndexChange(index: number) {
    this.tabIndex = index;
    const tab = this.tabMap[index];
    this.activeTab = tab;

    // Lazy load data when tab is first viewed
    if (tab === 'teams' && !this.teamsLoaded) {
      this.loadTeamRegistrations();
    } else if (tab === 'individuals' && !this.individualsLoaded) {
      this.loadIndividualRegistrations();
    } else if (tab === 'matches' && !this.matchesLoaded) {
      this.loadMatches();
    }
  }

  private setTabByName(tab: 'details' | 'teams' | 'individuals' | 'create-team' | 'matches') {
    this.activeTab = tab;
    this.tabIndex = this.tabMap.indexOf(tab);
  }

  getStatus(): string {
    if (!this.tournamentBasics) return '';
    const now = new Date();
    const start = new Date(this.tournamentBasics.startDate);
    const end = new Date(this.tournamentBasics.endDate);

    if (now < start) return 'Upcoming';
    if (now > end) return 'Completed';
    return 'Ongoing';
  }

  getStatusClass(): string {
    const status = this.getStatus();
    return `status-${status.toLowerCase()}`;
  }

  goBack() {
    this.router.navigate(['/tournaments']);
  }

  formatDate(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  calculateAge(dateOfBirth: string | null): number {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  toggleTeam(teamId: string) {
    if (this.expandedTeams.has(teamId)) {
      this.expandedTeams.delete(teamId);
    } else {
      this.expandedTeams.add(teamId);
    }
  }

  isTeamExpanded(teamId: string): boolean {
    return this.expandedTeams.has(teamId);
  }

  getSortedPlayers(players: any[]): any[] {
    return [...players].sort((a, b) => {
      // Captain always comes first
      if (a.isCaptain && !b.isCaptain) return -1;
      if (!a.isCaptain && b.isCaptain) return 1;
      return 0;
    });
  }

  // Team Registration Methods
  getEmptyTeamForm(): TeamRegistrationRequest {
    return {
      tournamentId: this.tournamentId,
      teamName: '',
      jerseyColor: null,
      nearestBranch: null,
      players: this.getEmptyPlayers()
    };
  }

  getEmptyPlayers(): PlayerDto[] {
    return [];
  }

  openTeamModal() {
    this.teamForm = this.getEmptyTeamForm();
    this.teamForm.tournamentId = this.tournamentId;
    this.showTeamModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeTeamModal() {
    this.showTeamModal = false;
    this.teamForm = this.getEmptyTeamForm();
  }

  addPlayer() {
    this.teamForm.players.push({
      fullName: '',
      dateOfBirth: null,
      skillLevel: '',
      phoneNumber: '',
      isCaptain: false
    });
  }

  removePlayer(index: number) {
    if (this.teamForm.players.length > 0) {
      this.teamForm.players.splice(index, 1);
    }
  }

  setCaptain(index: number) {
    this.teamForm.players.forEach((player, i) => {
      player.isCaptain = i === index;
    });
  }

  submitTeamRegistration() {
    this.isSubmittingTeam = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Convert date strings to Date objects
    const request = {
      ...this.teamForm,
      players: this.teamForm.players.map(p => ({
        ...p,
        dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth) : null
      }))
    };

    this.tournamentService.registerTeam(request).subscribe({
      next: (response) => {
        this.isSubmittingTeam = false;
        if (response.success) {
          this.successMessage = 'Team registration submitted successfully';
          this.closeTeamModal();
          this.loadTournament();
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isSubmittingTeam = false;
        this.errorMessage = error.error?.message || 'Failed to submit team registration';
        console.error('Error submitting team registration:', error);
      }
    });
  }

  // Individual Registration Methods
  getEmptyIndividualForm(): IndividualRegistrationRequest {
    return {
      tournamentId: this.tournamentId,
      fullName: '',
      gender: null,
      dateOfBirth: null,
      preferredPosition: null,
      skillLevel: null,
      phoneNumber: null,
      nearestBranch: null
    };
  }

  openIndividualModal() {
    this.individualForm = this.getEmptyIndividualForm();
    this.individualForm.tournamentId = this.tournamentId;
    this.showIndividualModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeIndividualModal() {
    this.showIndividualModal = false;
    this.individualForm = this.getEmptyIndividualForm();
  }

  submitIndividualRegistration() {
    this.isSubmittingIndividual = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Convert date string to Date object
    const request = {
      ...this.individualForm,
      dateOfBirth: this.individualForm.dateOfBirth ? new Date(this.individualForm.dateOfBirth) : null
    };

    this.tournamentService.registerIndividual(request).subscribe({
      next: (response) => {
        this.isSubmittingIndividual = false;
        if (response.success) {
          this.successMessage = 'Individual registration submitted successfully';
          this.closeIndividualModal();
          this.loadTournament();
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isSubmittingIndividual = false;
        this.errorMessage = error.error?.message || 'Failed to submit individual registration';
        console.error('Error submitting individual registration:', error);
      }
    });
  }

  openCreateTeamModal() {
    this.showCreateTeamModal = true;
  }

  closeCreateTeamModal() {
    this.showCreateTeamModal = false;
  }

  onTeamCreated() {
    // Reload both teams and individuals since individuals were moved to team
    this.loadTeamRegistrations();
    this.loadIndividualRegistrations();
    this.showCreateTeamModal = false;
    this.setTabByName('teams'); // Switch to teams tab
    this.successMessage = 'Team created successfully from individual registrations!';
    setTimeout(() => this.successMessage = '', 5000);
  }

  // Payment Methods
  onPaymentFilterChange() {
    // Reset to page 1 when filter changes
    this.teamCurrentPage = 1;
    this.individualCurrentPage = 1;
    this.teamsLoaded = false;
    this.individualsLoaded = false;

    // Reload only current tab
    if (this.activeTab === 'teams') {
      this.loadTeamRegistrations();
    } else if (this.activeTab === 'individuals') {
      this.loadIndividualRegistrations();
    }
  }

  getPaymentStatusBadgeClass(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Unpaid:
        return 'badge-red';
      case PaymentStatus.PartiallyPaid:
        return 'badge-yellow';
      case PaymentStatus.FullyPaid:
        return 'badge-green';
      case PaymentStatus.Overpaid:
        return 'badge-purple';
      default:
        return 'badge-gray';
    }
  }

  getPaymentStatusLabel(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Unpaid:
        return 'Unpaid';
      case PaymentStatus.PartiallyPaid:
        return 'Partially Paid';
      case PaymentStatus.FullyPaid:
        return 'Fully Paid';
      case PaymentStatus.Overpaid:
        return 'Overpaid';
      default:
        return 'Unknown';
    }
  }

  openPaymentModal(registrationId: string, type: 'team' | 'individual', remainingBalance: number) {
    this.selectedRegistrationId = registrationId;
    this.selectedRegistrationType = type;
    this.paymentForm = {
      amount: remainingBalance,
      paymentMethod: 'Cash',
      transactionReference: undefined,
      notes: undefined,
      paymentDate: new Date().toISOString().split('T')[0]
    };
    this.showPaymentModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closePaymentModal() {
    this.showPaymentModal = false;
    this.selectedRegistrationId = '';
    this.isRecordingPayment = false;
    this.paymentErrorMessage = '';
  }

  recordPayment() {
    this.isRecordingPayment = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.paymentErrorMessage = '';

    const request: RecordPaymentRequest = {
      ...this.paymentForm,
      paymentDate: new Date(this.paymentForm.paymentDate).toISOString()
    };

    const paymentObservable = this.selectedRegistrationType === 'team'
      ? this.tournamentService.recordTeamPayment(this.selectedRegistrationId, request)
      : this.tournamentService.recordIndividualPayment(this.selectedRegistrationId, request);

    paymentObservable.subscribe({
      next: (response) => {
        this.isRecordingPayment = false;
        if (response.success) {
          this.successMessage = 'Payment recorded successfully';
          this.closePaymentModal();

          // Reload only the affected registration type
          if (this.selectedRegistrationType === 'team') {
            this.loadTeamRegistrations();
          } else {
            this.loadIndividualRegistrations();
          }

          // Reload payment summary to update stats
          this.loadPaymentSummary();

          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isRecordingPayment = false;
        this.paymentErrorMessage = error.error?.message || 'Failed to record payment';
        console.error('Error recording payment:', error);
      }
    });
  }

  getSelectedRegistrationFee(): number {
    const registration = this.selectedRegistrationType === 'team'
      ? this.teamRegistrationsData?.registrations.registrations.find(t => t.id === this.selectedRegistrationId)
      : this.individualRegistrationsData?.registrations.registrations.find(i => i.id === this.selectedRegistrationId);
    return registration?.appliedFee || 0;
  }

  getSelectedRegistrationPaid(): number {
    const registration = this.selectedRegistrationType === 'team'
      ? this.teamRegistrationsData?.registrations.registrations.find(t => t.id === this.selectedRegistrationId)
      : this.individualRegistrationsData?.registrations.registrations.find(i => i.id === this.selectedRegistrationId);
    return registration?.totalPaid || 0;
  }

  getSelectedRegistrationBalance(): number {
    const registration = this.selectedRegistrationType === 'team'
      ? this.teamRegistrationsData?.registrations.registrations.find(t => t.id === this.selectedRegistrationId)
      : this.individualRegistrationsData?.registrations.registrations.find(i => i.id === this.selectedRegistrationId);
    return registration?.remainingBalance || 0;
  }

  getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  isPaymentFormValid(): boolean {
    const balance = this.getSelectedRegistrationBalance();
    return this.paymentForm.amount > 0 &&
           this.paymentForm.amount <= balance &&
           this.paymentForm.paymentMethod.trim() !== '' &&
           this.paymentForm.paymentDate.trim() !== '';
  }

  // Payment summary methods
  loadPaymentSummary() {
    this.isLoadingPaymentSummary = true;
    this.tournamentService.getPaymentSummary(this.tournamentId).subscribe({
      next: (response) => {
        this.paymentSummary = response.data ?? null;
        this.isLoadingPaymentSummary = false;
      },
      error: (error) => {
        console.error('Error loading payment summary:', error);
        this.isLoadingPaymentSummary = false;
      }
    });
  }

  // Detail panel methods
  openDetailPanel(registration: any, type: 'team' | 'individual') {
    this.selectedRegistration = registration;
    this.selectedRegistrationTypeForDetail = type;
    this.isEditingRegistration = false;
    this.showDetailPanel = true;

    // Populate edit form with current values
    if (type === 'team') {
      this.registrationEditForm = {
        teamName: registration.teamName,
        jerseyColor: registration.jerseyColor,
        nearestBranch: registration.nearestBranch,
        isAcademyMember: registration.isAcademyMember
      };
    } else {
      this.registrationEditForm = {
        fullName: registration.fullName,
        gender: registration.gender,
        dateOfBirth: registration.dateOfBirth,
        preferredPosition: registration.preferredPosition,
        skillLevel: registration.skillLevel,
        phoneNumber: registration.phoneNumber,
        nearestBranch: registration.nearestBranch,
        isAcademyMember: registration.isAcademyMember
      };
    }
  }

  closeDetailPanel() {
    this.showDetailPanel = false;
    this.selectedRegistration = null;
    this.selectedRegistrationTypeForDetail = null;
    this.isEditingRegistration = false;
  }

  toggleEditMode() {
    this.isEditingRegistration = !this.isEditingRegistration;
  }

  saveRegistrationChanges() {
    if (!this.selectedRegistration || !this.selectedRegistrationTypeForDetail) return;

    if (this.selectedRegistrationTypeForDetail === 'team') {
      this.isSavingRegistration = true;
      const request: UpdateTeamRegistrationRequest = {
        teamName: this.registrationEditForm.teamName,
        jerseyColor: this.registrationEditForm.jerseyColor || null,
        nearestBranch: this.registrationEditForm.nearestBranch || null,
      };

      this.tournamentService.updateTeamRegistration(this.selectedRegistration.id, request).subscribe({
        next: (response) => {
          this.isSavingRegistration = false;
          if (response.success) {
            this.isEditingRegistration = false;
            this.loadTeamRegistrations();
            this.closeDetailPanel();
            this.successMessage = 'Team updated successfully';
            setTimeout(() => this.successMessage = '', 3000);
          }
        },
        error: (error) => {
          this.isSavingRegistration = false;
          this.errorMessage = error.error?.message || 'Failed to update team';
          setTimeout(() => this.errorMessage = '', 5000);
        }
      });
    } else {
      // Individual update not yet implemented
      this.isEditingRegistration = false;
      this.closeDetailPanel();
    }
  }

  // Add player methods
  openAddPlayerForm() {
    this.showAddPlayerForm = true;
    this.addPlayerForm = {
      fullName: '',
      gender: null,
      dateOfBirth: null,
      preferredPosition: null,
      skillLevel: null,
      phoneNumber: null,
      isCaptain: false,
    };
  }

  closeAddPlayerForm() {
    this.showAddPlayerForm = false;
  }

  submitAddPlayer() {
    if (!this.selectedRegistration || !this.addPlayerForm.fullName.trim()) return;

    this.isAddingPlayer = true;
    this.tournamentService.addPlayerToTeam(this.selectedRegistration.id, this.addPlayerForm).subscribe({
      next: (response) => {
        this.isAddingPlayer = false;
        if (response.success) {
          this.showAddPlayerForm = false;
          this.loadTeamRegistrations();
          this.closeDetailPanel();
          this.successMessage = 'Player added successfully';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isAddingPlayer = false;
        this.errorMessage = error.error?.message || 'Failed to add player';
        setTimeout(() => this.errorMessage = '', 5000);
      }
    });
  }

  removeTeamPlayer(playerId: string) {
    if (!this.selectedRegistration) return;

    this.tournamentService.removePlayerFromTeam(this.selectedRegistration.id, playerId).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadTeamRegistrations();
          this.closeDetailPanel();
          this.successMessage = 'Player removed successfully';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Failed to remove player';
        setTimeout(() => this.errorMessage = '', 5000);
      }
    });
  }

  // Pagination handlers
  onTeamPageChange(page: number) {
    this.teamCurrentPage = page;
    this.loadTeamRegistrations(); // Only reloads teams, not entire tournament
  }

  onIndividualPageChange(page: number) {
    this.individualCurrentPage = page;
    this.loadIndividualRegistrations(); // Only reloads individuals, not entire tournament
  }

  onBranchFilterChange() {
    // Reset to page 1 when filter changes
    this.teamCurrentPage = 1;
    this.individualCurrentPage = 1;
    this.teamsLoaded = false;
    this.individualsLoaded = false;

    // Reload only current tab
    if (this.activeTab === 'teams') {
      this.loadTeamRegistrations();
    } else if (this.activeTab === 'individuals') {
      this.loadIndividualRegistrations();
    }
  }

  // ── Matches ────────────────────────────────────────────────────────────────

  getEmptyMatchForm(): CreateMatchRequest {
    return {
      tournamentId: this.tournamentId,
      homeTeamName: '',
      awayTeamName: '',
      scheduledAt: '',
      branchId: null,
      stage: null,
      notes: ''
    };
  }

  loadMatches() {
    this.isLoadingMatches = true;
    this.tournamentService.getMatches(this.tournamentId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.matches = response.data;
        }
        this.matchesLoaded = true;
        this.isLoadingMatches = false;
      },
      error: (error) => {
        console.error('Error loading matches:', error);
        this.isLoadingMatches = false;
      }
    });
  }

  loadRegisteredTeams() {
    if (this.registeredTeamsLoaded) return;
    this.isLoadingRegisteredTeams = true;
    this.tournamentService.getTeamRegistrations(this.tournamentId, undefined, undefined, 1, 500).subscribe({
      next: (response) => {
        if (response.data) {
          this.registeredTeams = response.data.registrations.registrations;
        }
        this.registeredTeamsLoaded = true;
        this.isLoadingRegisteredTeams = false;
      },
      error: () => { this.isLoadingRegisteredTeams = false; }
    });
  }

  get filteredHomeTeams(): TeamRegistration[] {
    const raw = this.homeTeamFilter;
    const query = (typeof raw === 'string' ? raw : '').toLowerCase();
    return this.registeredTeams
      .filter(t => t.teamName.toLowerCase().includes(query))
      .sort((a, b) => a.teamName.localeCompare(b.teamName));
  }

  get filteredAwayTeams(): TeamRegistration[] {
    const raw = this.awayTeamFilter;
    const query = (typeof raw === 'string' ? raw : '').toLowerCase();
    return this.registeredTeams
      .filter(t => t.teamName.toLowerCase().includes(query))
      .sort((a, b) => a.teamName.localeCompare(b.teamName));
  }

  displayTeamName = (team: TeamRegistration): string => {
    return team?.teamName || '';
  }

  onTeamSelect(side: 'home' | 'away', team: TeamRegistration) {
    if (!team) return;
    if (side === 'home') {
      this.matchForm.homeTeamName = team.teamName;
      this.matchForm.homeTeamRegistrationId = team.id;
    } else {
      this.matchForm.awayTeamName = team.teamName;
      this.matchForm.awayTeamRegistrationId = team.id;
    }
  }

  openCreateMatchModal() {
    this.isEditingMatch = false;
    this.selectedMatch = null;
    this.matchForm = this.getEmptyMatchForm();
    this.matchErrorMessage = '';
    this.homeTeamFilter = '';
    this.awayTeamFilter = '';
    this.loadRegisteredTeams();
    this.showMatchModal = true;
  }

  openEditMatchModal(match: Match) {
    this.isEditingMatch = true;
    this.selectedMatch = match;
    // Convert UTC datetime to local datetime-local input format (null for draft)
    const pad = (n: number) => n.toString().padStart(2, '0');
    let localStr = '';
    if (match.scheduledAt) {
      const local = new Date(match.scheduledAt);
      localStr = `${local.getFullYear()}-${pad(local.getMonth()+1)}-${pad(local.getDate())}T${pad(local.getHours())}:${pad(local.getMinutes())}`;
    }
    this.matchEditForm = {
      homeTeamName: match.homeTeamName,
      awayTeamName: match.awayTeamName,
      homeScore: match.homeScore,
      awayScore: match.awayScore,
      status: match.status,
      scheduledAt: localStr,
      branchId: match.branchId,
      stage: match.stage,
      notes: match.notes
    };
    this.matchErrorMessage = '';
    this.showMatchModal = true;
  }

  closeMatchModal() {
    this.showMatchModal = false;
    this.selectedMatch = null;
    this.matchErrorMessage = '';
  }

  saveMatch() {
    if (this.isEditingMatch) {
      this.updateMatch();
    } else {
      this.createMatch();
    }
  }

  createMatch() {
    if (!this.matchForm.homeTeamName || !this.matchForm.awayTeamName) {
      this.matchErrorMessage = 'Please select both teams.';
      return;
    }
    this.isSavingMatch = true;
    this.matchErrorMessage = '';
    const request: CreateMatchRequest = {
      ...this.matchForm,
      tournamentId: this.tournamentId,
      scheduledAt: this.matchForm.scheduledAt ? new Date(this.matchForm.scheduledAt).toISOString() : null
    };
    this.tournamentService.createMatch(request).subscribe({
      next: (response) => {
        this.isSavingMatch = false;
        if (response.success && response.data) {
          this.matches = [...this.matches, response.data];
          this.closeMatchModal();
          this.successMessage = 'Match created successfully';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isSavingMatch = false;
        this.matchErrorMessage = error.error?.message || 'Failed to create match';
      }
    });
  }

  updateMatch() {
    if (!this.selectedMatch) return;
    this.isSavingMatch = true;
    this.matchErrorMessage = '';
    const request: UpdateMatchRequest = {
      homeScore: this.matchEditForm.homeScore,
      awayScore: this.matchEditForm.awayScore,
      status: this.matchEditForm.status,
      scheduledAt: this.matchEditForm.scheduledAt
        ? new Date(this.matchEditForm.scheduledAt).toISOString()
        : undefined,
      branchId: this.matchEditForm.branchId,
      stage: this.matchEditForm.stage,
      notes: this.matchEditForm.notes
    };
    this.tournamentService.updateMatch(this.selectedMatch.id, request).subscribe({
      next: (response) => {
        this.isSavingMatch = false;
        if (response.success && response.data) {
          this.matches = this.matches.map(m => m.id === response.data!.id ? response.data! : m);
          this.closeMatchModal();
          this.successMessage = 'Match updated successfully';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isSavingMatch = false;
        this.matchErrorMessage = error.error?.message || 'Failed to update match';
      }
    });
  }

  deleteMatch(match: Match) {
    if (!confirm(`Delete match: ${match.homeTeamName} vs ${match.awayTeamName}?`)) return;
    this.tournamentService.deleteMatch(match.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.matches = this.matches.filter(m => m.id !== match.id);
          this.successMessage = 'Match deleted';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Failed to delete match';
      }
    });
  }

  getMatchesByStage(): { stage: string; matches: Match[] }[] {
    const stageOrder = ['Group Stage', 'Knockout', 'Quarter-finals', 'Semi-finals', 'Final', 'Other'];
    const grouped = new Map<string, Match[]>();

    for (const m of this.matches) {
      if (this.matchFilterStage && m.stage !== this.matchFilterStage) continue;
      if (this.matchFilterGroup && m.groupName !== this.matchFilterGroup) continue;
      if (this.matchFilterTeam && m.homeTeamName !== this.matchFilterTeam && m.awayTeamName !== this.matchFilterTeam) continue;
      if (this.matchFilterDate && m.scheduledAt) {
        const matchDate = new Date(m.scheduledAt).toISOString().slice(0, 10);
        if (matchDate !== this.matchFilterDate) continue;
      } else if (this.matchFilterDate && !m.scheduledAt) {
        continue;
      }
      const key = m.stage || 'Other';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(m);
    }

    return stageOrder
      .filter(s => grouped.has(s))
      .map(s => ({ stage: s, matches: grouped.get(s)!.sort((a, b) => (a.scheduledAt ? new Date(a.scheduledAt).getTime() : Infinity) - (b.scheduledAt ? new Date(b.scheduledAt).getTime() : Infinity)) }));
  }

  get matchStageOptions(): string[] {
    const stages = new Set(this.matches.map(m => m.stage).filter((s): s is string => !!s));
    return ['Group Stage', 'Knockout', 'Quarter-finals', 'Semi-finals', 'Final'].filter(s => stages.has(s));
  }

  get matchGroupOptions(): string[] {
    const names = new Set(this.matches.map(m => m.groupName).filter((g): g is string => !!g));
    return Array.from(names).sort();
  }

  get matchTeamOptions(): string[] {
    const teams = new Set<string>();
    for (const m of this.matches) {
      if (m.homeTeamName) teams.add(m.homeTeamName);
      if (m.awayTeamName) teams.add(m.awayTeamName);
    }
    return Array.from(teams).sort();
  }

  get filteredMatchTeamOptions(): string[] {
    const q = this.matchFilterTeamSearch?.toLowerCase() || '';
    if (!q) return this.matchTeamOptions;
    return this.matchTeamOptions.filter(t => t.toLowerCase().includes(q));
  }

  onMatchTeamFilterSelect(teamName: string) {
    this.matchFilterTeam = teamName;
    this.matchFilterTeamSearch = teamName;
  }

  displayMatchTeamFilter = (value: string): string => value || '';

  get hasKnockoutMatches(): boolean {
    return this.matches.some(m => m.stage && m.stage !== 'Group Stage');
  }

  onMatchUpdatedFromBracket(match: Match) {
    // Update the match in the local array
    this.matches = this.matches.map(m => m.id === match.id ? match : m);
    this.successMessage = 'Match updated successfully';
    setTimeout(() => this.successMessage = '', 3000);
    // Reload matches to pick up any auto-advance changes from the API
    this.tournamentService.getMatches(this.tournamentId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.matches = response.data;
        }
      }
    });
  }

  getMatchStatusClass(status: string): string {
    switch (status) {
      case 'live': return 'status-ongoing';
      case 'completed': return 'status-completed';
      case 'draft': return 'status-draft';
      default: return 'status-upcoming';
    }
  }

  formatMatchDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  // ── Group generation ───────────────────────────────────────────────────────

  openGroupModal() {
    this.groupForm = { groupName: '', stage: 'Group Stage', selectedTeamIds: new Set() };
    this.groupPreview = [];
    this.groupErrorMessage = '';
    this.loadRegisteredTeams();
    this.showGroupModal = true;
  }

  closeGroupModal() {
    this.showGroupModal = false;
    this.groupErrorMessage = '';
  }

  toggleGroupTeam(teamId: string) {
    if (this.groupForm.selectedTeamIds.has(teamId)) {
      this.groupForm.selectedTeamIds.delete(teamId);
    } else {
      this.groupForm.selectedTeamIds.add(teamId);
    }
    this.updateGroupPreview();
  }

  isGroupTeamSelected(teamId: string): boolean {
    return this.groupForm.selectedTeamIds.has(teamId);
  }

  updateGroupPreview() {
    const ids = Array.from(this.groupForm.selectedTeamIds);
    const teams = ids.map(id => this.registeredTeams.find(t => t.id === id)!).filter(Boolean);
    const pairs: { home: string; away: string }[] = [];
    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        pairs.push({ home: teams[i].teamName, away: teams[j].teamName });
      }
    }
    this.groupPreview = pairs;
  }

  generateGroup() {
    if (!this.groupForm.groupName.trim()) {
      this.groupErrorMessage = 'Please enter a group name (e.g. "Group A").';
      return;
    }
    if (this.groupForm.selectedTeamIds.size < 2) {
      this.groupErrorMessage = 'Select at least 2 teams to generate matches.';
      return;
    }
    this.isGeneratingGroup = true;
    this.groupErrorMessage = '';
    const request: GenerateGroupMatchesRequest = {
      tournamentId: this.tournamentId,
      groupName: this.groupForm.groupName.trim(),
      teamRegistrationIds: Array.from(this.groupForm.selectedTeamIds),
      stage: this.groupForm.stage
    };
    this.tournamentService.generateGroupMatches(request).subscribe({
      next: (response) => {
        this.isGeneratingGroup = false;
        if (response.success && response.data) {
          const group = response.data;
          this.groups = [...this.groups, group];
          this.matches = [...this.matches, ...group.matches];
          this.closeGroupModal();
          this.successMessage = `Generated ${group.matches.length} matches for "${group.name}"`;
          setTimeout(() => this.successMessage = '', 4000);
        }
      },
      error: (error) => {
        this.isGeneratingGroup = false;
        this.groupErrorMessage = error.error?.message || 'Failed to generate matches';
      }
    });
  }
}
